import Dropdown from "../Dropdown.jsx";
import {useContext, useEffect, useMemo, useState} from "react";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {useTranslation} from "react-i18next";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import {ProjectApi} from "../../api/authority/generated/index";
import Spinner, {SpinnerSize} from "../Spinner.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {isPreview} from "../../utils/environment.js";
import ProjectRuntimeOverviewCard from "../runtime/ProjectRuntimeOverviewCard.jsx";
import {getOverviewIndicator} from "../runtime/runtimeStatusUtils.js";

const projectStatusPromises = new Map();
const projectStatusCache = new Map();

function fetchProjectStatus(projectId, authorityConfiguration) {
    const cachedPromise = projectStatusPromises.get(projectId);
    if (cachedPromise) {
        return cachedPromise;
    }

    const promise = new ProjectApi(authorityConfiguration)
        .projectStatus({projectId})
        .then((response) => {
            projectStatusCache.set(projectId, response);
            return response;
        })
        .finally(() => {
            projectStatusPromises.delete(projectId);
        });

    projectStatusPromises.set(projectId, promise);

    return promise;
}

export default function UpdateDropdown() {
    const {context, getAuthorityConfiguration} = useContext(GlobalContext);
    const {t} = useTranslation();
    const {et} = useErrorTranslation();

    const [data, setData] = useState(null);
    const [noProject, setNoProject] = useState(false);
    const [demoLoaded, setDemoLoaded] = useState(false);
    const [refreshState, setRefreshState] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const currentProject = useMemo(() => {
        return Object.values(context.currentUserProjects ?? {}).find(
            (possibleProject) => possibleProject.id === context.currentProjectId,
        ) ?? null;
    }, [context.currentProjectId, context.currentUserProjects]);

    useEffect(() => {
        setNoProject(false);
        setDemoLoaded(false);

        if (isPreview()) {
            setDemoLoaded(true);
            setIsLoading(false);
            setData({
                projectRuntime: {
                    projectId: context.currentProjectId,
                    status: "idle",
                    phase: null,
                    lastPublishedAt: new Date().toISOString(),
                    nextScheduledRunAt: null,
                    nextScheduledRunMode: null,
                    activeRunId: null,
                    activeRunMode: null,
                    queuedAt: null,
                    startedAt: null,
                    since: null,
                    until: null,
                    canManualRebuild: false,
                    error: null,
                },
                integrations: [],
            });
            return;
        }

        if (!currentProject) {
            setIsLoading(false);
            setData(null);
            setNoProject(true);
            return;
        }

        const cachedData = projectStatusCache.get(currentProject.id) ?? null;
        if (cachedData !== null) {
            setData(cachedData);
        }

        setIsLoading(cachedData === null);

        fetchProjectStatus(currentProject.id, getAuthorityConfiguration())
            .then(setData)
            .catch((e) => et(e).then(toast.error))
            .finally(() => setIsLoading(false));

        const timeoutId = setTimeout(() => {
            setRefreshState((current) => current + 1);
        }, 1000 * 60);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [context.currentProjectId, context.currentUserProjectsLoaded, currentProject, refreshState]);

    const indicator = getOverviewIndicator(t, data);

    return (
        <Dropdown
            align={"right"}
            type={"modern"}
            title={((data === null && isLoading) || noProject)
                ? <Spinner size={SpinnerSize["spinner-small"]}/>
                : (
                    <div>
                        <FontAwesomeIcon
                            icon={indicator.icon}
                            className={indicator.colorClass + (indicator.spin ? " animate-spin" : "")}
                        />
                    </div>
                )}
            showTitle={false}
        >
            {noProject && <div>{t("project.awaiting-project", {defaultValue: "Čekáme na výběr projektu"})}</div>}
            {demoLoaded && <div className={"pb-2 text-sm text-slate-500 dark:text-slate-300"}>{t("project.demo-data-loaded", {defaultValue: "Ukázková data jsou už připravená"})}</div>}

            {data !== null && currentProject && (
                <div className={"w-[26rem]"}>
                    <ProjectRuntimeOverviewCard
                        projectData={currentProject}
                        projectRuntime={data.projectRuntime}
                        integrations={data.integrations ?? []}
                        compact={true}
                        showProjectIdentity={false}
                    />
                </div>
            )}
        </Dropdown>
    );
}
